import React from "react";
import { useOfflineQueue } from "../../hooks/useOfflineQueue";
import "./OfflineIndicator.css";

/**
 * Indicador de estado offline y mensajes pendientes
 * Muestra:
 * - Banner de "Sin conexión" cuando está offline
 * - Badge con contador de mensajes pendientes
 * - Opcionalmente, lista de mensajes en cola
 */
export const OfflineIndicator = ({ showDetails = false }) => {
  const {
    isOnline,
    pendingMessages,
    pendingCount,
    isBackgroundSyncSupported,
    retryAll,
    deleteMessage
  } = useOfflineQueue();
  
  // No mostrar nada si está online y no hay pendientes
  if (isOnline && pendingCount === 0) {
    return null;
  }
  
  return (
    <div className="offline-indicator">
      {/* Banner de offline */}
      {!isOnline && (
        <div className="offline-banner">
          <span className="offline-icon">📡</span>
          <span>Sin conexión - Los mensajes se enviarán automáticamente</span>
        </div>
      )}
      
      {/* Badge de mensajes pendientes */}
      {pendingCount > 0 && (
        <div className="pending-messages-badge">
          <span className="pending-icon">⏳</span>
          <span className="pending-count">
            {pendingCount} mensaje{pendingCount !== 1 ? "s" : ""} pendiente{pendingCount !== 1 ? "s" : ""}
          </span>
          
          {/* Botón de retry manual si está online */}
          {isOnline && (
            <button 
              className="retry-button"
              onClick={retryAll}
              title="Reintentar envío"
            >
              🔄 Reintentar
            </button>
          )}
        </div>
      )}
      
      {/* Lista detallada de mensajes pendientes */}
      {showDetails && pendingMessages.length > 0 && (
        <div className="pending-messages-list">
          <h4>Mensajes en cola:</h4>
          {pendingMessages.map((msg) => (
            <div 
              key={msg.id} 
              className={`pending-message pending-message--${msg.status}`}
            >
              <div className="pending-message-header">
                <span className="pending-message-contact">
                  {msg.contactName || msg.contactId}
                </span>
                <span className="pending-message-status">
                  {msg.status === "pending" && "⏳ Pendiente"}
                  {msg.status === "sending" && "📤 Enviando..."}
                  {msg.status === "sent" && "✅ Enviado"}
                  {msg.status === "failed" && "❌ Falló"}
                </span>
              </div>
              
              <div className="pending-message-body">
                {msg.message.substring(0, 100)}
                {msg.message.length > 100 && "..."}
              </div>
              
              {msg.error && (
                <div className="pending-message-error">
                  Error: {msg.error}
                </div>
              )}
              
              <div className="pending-message-actions">
                <button
                  className="delete-button"
                  onClick={() => msg.id && deleteMessage(msg.id)}
                  title="Eliminar mensaje"
                >
                  🗑️
                </button>
              </div>
            </div>
          ))}
          
          {!isBackgroundSyncSupported && (
            <div className="sync-warning">
              ⚠️ Tu navegador no soporta Background Sync. 
              Los mensajes se enviarán cuando abras la app con conexión.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OfflineIndicator;
